// src/components/common/ChartModeToggle.tsx
import React, {useEffect, useMemo, useState} from 'react';

export type ChartMode = 'absolute' | 'normalized';

// ─── Toggle button component ──────────────────────────────────────────────────
interface ChartModeToggleProps {
   mode: ChartMode;
   onModeChange: (mode: ChartMode) => void;
}

export const ChartModeToggle: React.FC<ChartModeToggleProps> = ({mode, onModeChange}) => (
   <div className="flex items-center bg-gray-800 border border-gray-700 rounded-lg p-0.5 text-xs">
      <button
         onClick={() => onModeChange('absolute')}
         className={`px-2.5 py-1 rounded-md transition-all ${
            mode === 'absolute'
               ? 'bg-gray-600 text-white'
               : 'text-gray-400 hover:text-gray-200'
         }`}
      >
         Absolute
      </button>
      <button
         onClick={() => onModeChange('normalized')}
         className={`px-2.5 py-1 rounded-md transition-all ${
            mode === 'normalized'
               ? 'bg-gray-600 text-white'
               : 'text-gray-400 hover:text-gray-200'
         }`}
      >
         Normalized
      </button>
   </div>
);

// ─── Info message for normalized mode ─────────────────────────────────────────
interface NormalizedInfoProps {
   show: boolean;
}

export const NormalizedInfo: React.FC<NormalizedInfoProps> = ({show}) => {
   if (!show) return null;
   return (
      <p className="text-xs text-gray-500 mb-3 bg-gray-800 rounded-lg px-3 py-1.5">
         Each metric is scaled to its own 0–100% range so trends are visible regardless of magnitude.
         Hover for real values.
      </p>
   );
};

// ─── Hook for chart normalization logic ───────────────────────────────────────
export function useChartNormalization<T extends Record<string, any>>(
   chartData: T[],
   metricKeys: string[]
): { normalizedData: T[]; } {
   const normalizedData = useMemo(() => {
      if (chartData.length === 0) return [];
      
      const ranges: Record<string, { min: number; max: number }> = {};
      metricKeys.forEach(key => {
         const values = chartData
            .map(d => d[key] as number)
            .filter(v => v !== undefined && !isNaN(v));
         const min = Math.min(...values);
         const max = Math.max(...values);
         ranges[key] = {min, max};
      });
      
      return chartData.map(d => {
         const point: Record<string, any> = {tick: d.tick};
         // Preserve non-metric fields like timestamp
         Object.keys(d).forEach(k => {
            if (!metricKeys.includes(k) && k !== 'tick') {
               point[k] = d[k];
            }
         });
         // Normalize metric fields
         metricKeys.forEach(key => {
            const {min, max} = ranges[key];
            const range = max - min;
            const value = d[key] as number;
            point[key] = range === 0 ? 50 : ((value - min) / range) * 100;
         });
         return point as T;
      });
   }, [chartData, metricKeys]);
   
   return {normalizedData};
}

// ─── Hook combining state and normalization ───────────────────────────────────
export function useChartMode<T extends Record<string, any>>(
   chartData: T[],
   metricKeys: string[]
) {
   const [chartMode, setChartMode] = useState<ChartMode>('absolute');
   const {normalizedData} = useChartNormalization(chartData, metricKeys);
   const displayData = chartMode === 'normalized' ? normalizedData : chartData;
   
   return {
      chartMode,
      setChartMode,
      displayData,
      normalizedData,
   };
}

// ─── Hook for range slicing ───────────────────────────────────────────────────
export function useChartRange<T extends { tick: number }>(data: T[]) {
   const total = data.length;
   const [range, setRange] = useState<[number, number]>([0, 100]);
   
   // Reset to full range when new data arrives
   useEffect(() => {
      setRange([0, 100]);
   }, [total]);
   
   const sliced = useMemo(() => {
      if (total === 0) return data;
      const start = Math.floor((range[0] / 100) * total);
      const end = Math.ceil((range[1] / 100) * total);
      return data.slice(start, end);
   }, [data, range, total]);
   
   return {range, setRange, sliced};
}

// ─── Range slider UI ──────────────────────────────────────────────────────────
interface ChartRangeSliderProps {
   range: [number, number];
   onChange: (range: [number, number]) => void;
   totalPoints: number;
}

export const ChartRangeSlider: React.FC<ChartRangeSliderProps> = ({range, onChange, totalPoints}) => {
   if (totalPoints < 2) return null;
   const visibleCount = Math.round(((range[1] - range[0]) / 100) * totalPoints);
   return (
      <div className="mt-2 space-y-1">
         <div className="flex items-center justify-between text-xs text-gray-500">
            <span>Range</span>
            <span className="font-mono">{visibleCount} of {totalPoints} snapshots</span>
         </div>
         <div className="flex items-center gap-2">
            <input
               type="range" min={0} max={range[1] - 5} value={range[0]}
               onChange={e => onChange([Number(e.target.value), range[1]])}
               className="flex-1 accent-blue-500 h-1"
            />
            <input
               type="range" min={range[0] + 5} max={100} value={range[1]}
               onChange={e => onChange([range[0], Number(e.target.value)])}
               className="flex-1 accent-blue-500 h-1"
            />
         </div>
         <div className="flex justify-between text-xs text-gray-600 font-mono">
            <span>{range[0]}%</span>
            <button
               onClick={() => onChange([0, 100])}
               className="text-gray-500 hover:text-gray-300 transition-colors"
            >reset
            </button>
            <span>{range[1]}%</span>
         </div>
      </div>
   );
};

export default ChartModeToggle;
